import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { getDayBook } from '@/services/firestore'
import { CalendarDays, RefreshCw } from 'lucide-react'

export const Route = createFileRoute('/admin/daybook')({ component: DayBookPage })

function DayBookPage() {
    const [entries, setEntries] = useState<any[]>([])
    const [date, setDate] = useState(new Date().toISOString().split('T')[0])
    const [loading, setLoading] = useState(false)

    useEffect(() => { load() }, [date])
    const load = async () => {
        setLoading(true)
        const data = await getDayBook(date)
        setEntries(data)
        setLoading(false)
    }

    const f = (n: number) => n.toLocaleString('en-PK', { minimumFractionDigits: 2 })
    const totalDebit = entries.reduce((s, e) => s + (e.debit || 0), 0)
    const totalCredit = entries.reduce((s, e) => s + (e.credit || 0), 0)

    return (
        <div className="p-4 pt-14 lg:pt-6 lg:p-6 bg-[#f0f5ff] min-h-screen">
            <div className="mb-5">
                <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2"><CalendarDays className="w-6 h-6 text-blue-500" />Day Book</h1>
                <p className="text-xs text-slate-500 mt-0.5">All journal entries for a selected date</p>
            </div>

            <div className="bg-white rounded-xl border border-slate-200/60 p-4 mb-4 shadow-sm flex items-center gap-4">
                <label className="text-xs font-bold text-slate-600 uppercase">Date:</label>
                <input type="date" value={date} onChange={e => setDate(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50 focus:ring-1 focus:ring-blue-500 focus:border-blue-500" />
                <span className="text-xs text-slate-400">{entries.length} entries found</span>
            </div>

            {loading ? (
                <div className="flex justify-center py-20"><RefreshCw className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : entries.length === 0 ? (
                <div className="text-center py-20 text-slate-400"><CalendarDays className="w-12 h-12 mx-auto mb-3 opacity-30" /><p className="text-sm font-semibold">No entries for {date}</p></div>
            ) : (
                <div className="bg-white rounded-xl border border-slate-200/60 shadow-sm overflow-hidden">
                    <table className="w-full text-sm">
                        <thead><tr className="bg-slate-50 border-b border-slate-100">
                            <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase">Account</th>
                            <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase">Narration</th>
                            <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase">Type</th>
                            <th className="text-right px-4 py-3 text-xs font-bold text-green-600 uppercase">Debit</th>
                            <th className="text-right px-4 py-3 text-xs font-bold text-red-600 uppercase">Credit</th>
                        </tr></thead>
                        <tbody>
                            {entries.map((e, i) => (
                                <tr key={e.id} className={`border-b border-slate-50 ${i % 2 ? 'bg-slate-25' : ''}`}>
                                    <td className="px-4 py-2.5 font-semibold text-slate-700 text-xs">{e.accountName}</td>
                                    <td className="px-4 py-2.5 text-xs text-slate-600">{e.narration}</td>
                                    <td className="px-4 py-2.5"><span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{e.referenceType?.replace('_', ' ')}</span></td>
                                    <td className="px-4 py-2.5 text-right font-mono text-green-600">{e.debit ? f(e.debit) : '—'}</td>
                                    <td className="px-4 py-2.5 text-right font-mono text-red-600">{e.credit ? f(e.credit) : '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr className="bg-slate-50 border-t-2 border-slate-200">
                                <td colSpan={3} className="px-4 py-3 text-right font-bold text-slate-700 text-sm">Totals:</td>
                                <td className="px-4 py-3 text-right font-mono font-bold text-green-700 text-sm">{f(totalDebit)}</td>
                                <td className="px-4 py-3 text-right font-mono font-bold text-red-700 text-sm">{f(totalCredit)}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}
        </div>
    )
}
